import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, CheckCircle, AlertCircle,
  Star, Crown, Clock, Radio, Target, Globe
} from "lucide-react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const TIERS = [
  {
    id: "free",
    label: "Free Submission",
    price: null,
    priceLabel: "Free",
    icon: Send,
    color: "purple",
    perks: [
      "Basic review by our team",
      "Added to rotation if approved",
      "Standard consideration timeline",
    ],
    cta: "Submit Free",
  },
  {
    id: "featured",
    label: "Featured",
    price: "$10",
    priceLabel: "$10 one-time",
    icon: Star,
    color: "yellow",
    badge: "POPULAR",
    perks: [
      "Priority review — reviewed first",
      "Featured badge on Discover page",
      "Higher placement in search & feeds",
    ],
    cta: "Get Featured",
  },
  {
    id: "membership",
    label: "Artist Membership",
    price: "$19.99",
    priceLabel: "$19.99/mo",
    icon: Crown,
    color: "green",
    badge: "BEST VALUE",
    perks: [
      "Unlimited submissions per month",
      "Guaranteed rotation placement",
      "Artist profile page on the platform",
      "Priority placement in all feeds",
    ],
    cta: "Join as Artist",
  },
];

const TIER_STYLES = {
  free: {
    border: "border-white/10 hover:border-purple-500/40",
    activeBorder: "border-purple-500/60 bg-purple-500/5",
    icon: "bg-purple-500/20 text-purple-400",
    badge: "",
    button: "bg-purple-500 hover:bg-purple-600 text-white",
    check: "text-purple-400",
  },
  featured: {
    border: "border-white/10 hover:border-yellow-500/40",
    activeBorder: "border-yellow-500/60 bg-yellow-500/5",
    icon: "bg-yellow-500/20 text-yellow-400",
    badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    button: "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black",
    check: "text-yellow-400",
  },
  membership: {
    border: "border-white/10 hover:border-emerald-400/40",
    activeBorder: "border-emerald-400/60 bg-emerald-400/5 shadow-[0_0_30px_rgba(16,185,129,0.15)]",
    icon: "bg-emerald-500/20 text-emerald-400",
    badge: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    button: "bg-gradient-to-r from-emerald-500 to-green-400 hover:from-emerald-600 hover:to-green-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]",
    check: "text-emerald-400",
    priceGlow: "text-emerald-400 drop-shadow-[0_0_12px_rgba(16,185,129,0.6)]",
  },
};

const SubmitPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [selectedTier, setSelectedTier] = useState("free");
  const [formData, setFormData] = useState({
    artist_name: "",
    track_title: "",
    genre: "",
    email: user?.email || "",
    streaming_link: "",
    pro_affiliation: "",
    message: "",
    featured: false,
    submitter_type: "artist",
    company_name: "",
    submitter_role: "",
    authorization_note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [featurePaid, setFeaturePaid] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [membershipRequested, setMembershipRequested] = useState(false);

  // Targeting state
  const [targetType, setTargetType] = useState("general");
  const [selectedStations, setSelectedStations] = useState([]);
  const [availableStations, setAvailableStations] = useState([]);
  const [bpm, setBpm] = useState("");
  const [isExplicit, setIsExplicit] = useState(false);
  const [targetsCreated, setTargetsCreated] = useState(0);

  // Fetch available stations for targeting
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/submission-targets/stations`)
      .then(r => r.json())
      .then(data => setAvailableStations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTierSelect = (tierId) => {
    setSelectedTier(tierId);
    setFormData({ ...formData, featured: tierId === "featured" });
  };

  const handleMembershipInterest = async () => {
    if (!formData.email || !formData.artist_name) {
      setError("Please fill in your artist name and email first.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/membership-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artist_name: formData.artist_name,
          email: formData.email,
          genre: formData.genre,
        }),
      });
      if (res.ok) {
        setMembershipRequested(true);
        setError("");
      } else {
        const data = await res.json();
        setError(data.detail || "Something went wrong.");
      }
    } catch {
      setError("Failed to connect. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.artist_name || !formData.track_title || !formData.email || !formData.pro_affiliation) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!agreedToTerms) {
      setError("You must agree to the Submission Terms to continue.");
      return;
    }
    if (targetType === "specific" && selectedStations.length === 0) {
      setError("Please select at least one station to target.");
      return;
    }
    setSubmitting(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const authToken = token || localStorage.getItem('token');
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const body = {
        ...formData,
        featured: selectedTier === "featured",
        target_type: targetType,
        station_ids: targetType === "specific" ? selectedStations : [],
        bpm: bpm ? parseInt(bpm) : null,
        is_explicit: isExplicit,
      };
      const res = await fetch(`${BACKEND_URL}/api/submission-targets/submit`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setTargetsCreated(data.targets_created || 0);
        setSubmitted(true);
      } else {
        const data = await res.json();
        setError(data.detail || "Submission failed. Please try again.");
      }
    } catch {
      setError("Failed to connect. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success State ──
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <SEO title="Track Submitted" description="Your track has been submitted to DJsandMCMedia for review." />
        <Header />
        <div className="flex items-center justify-center min-h-[80vh] px-6">
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold mb-4" data-testid="submit-success-heading">Track Submitted!</h1>
            <p className="text-white/60 mb-8">
              Your track has been submitted for review.
              {targetsCreated > 0 && (
                <span className="block mt-2 text-purple-400 font-semibold">
                  Sent to {targetsCreated} station{targetsCreated !== 1 ? 's' : ''} for consideration.
                </span>
              )}
              <span className="block mt-2">We've received your submission. Our team will review it and reach out if your track is selected for rotation on our live channels.</span>
            </p>

            {/* Featured Upsell — only show if they didn't select featured tier */}
            {selectedTier !== "featured" && !featurePaid && (
              <div className="bg-gradient-to-b from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-6 text-left" data-testid="feature-upsell">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-bold text-white">Want to skip the line? Get Featured — $10</h3>
                </div>
                <p className="text-white/60 text-sm mb-4">
                  Featured tracks get priority review, a badge on the Discover page, and top placement when approved.
                </p>
                <div id="feature-paypal-container" data-testid="feature-paypal">
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{ description: "DJsandMCMedia Featured Track - $10", amount: { value: "10.00", currency_code: "USD" } }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      await actions.order.capture();
                      const res = await fetch(`${BACKEND_URL}/api/submissions/feature`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: formData.email, track_title: formData.track_title, order_id: data.orderID }),
                      });
                      const result = await res.json();
                      if (result.status === "success") setFeaturePaid(true);
                      else setError(result.message || "Payment verification failed. Contact gary@djsandmcs.com");
                    }}
                    onError={(err) => console.error('PayPal error:', err)}
                  />
                </div>
              </div>
            )}

            {/* Featured PayPal — for users who selected featured tier */}
            {selectedTier === "featured" && !featurePaid && (
              <div className="bg-gradient-to-b from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-6 text-left" data-testid="feature-paypal-selected">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-bold text-white">Complete Your Featured Payment — $10</h3>
                </div>
                <p className="text-white/60 text-sm mb-4">
                  Pay now to activate priority review, your Featured badge, and top placement.
                </p>
                <div id="feature-paypal-selected-container" data-testid="feature-paypal">
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{ description: "DJsandMCMedia Featured Track - $10", amount: { value: "10.00", currency_code: "USD" } }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      await actions.order.capture();
                      const res = await fetch(`${BACKEND_URL}/api/submissions/feature`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: formData.email, track_title: formData.track_title, order_id: data.orderID }),
                      });
                      const result = await res.json();
                      if (result.status === "success") setFeaturePaid(true);
                      else setError(result.message || "Payment verification failed. Contact gary@djsandmcs.com");
                    }}
                    onError={(err) => console.error('PayPal error:', err)}
                  />
                </div>
              </div>
            )}

            {featurePaid && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="feature-confirmed">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm font-medium">Your track is now featured! You'll get priority review and top placement.</p>
              </div>
            )}

            <button
              onClick={() => navigate("/")}
              className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-xl transition font-semibold"
              data-testid="submit-go-home"
            >
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Membership Interest Success State ──
  if (membershipRequested) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <SEO title="Artist Membership" description="Join as an Artist Member on DJsandMCMedia." />
        <Header />
        <div className="flex items-center justify-center min-h-[80vh] px-6">
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 bg-amber-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold mb-4" data-testid="membership-success-heading">You're on the List!</h1>
            <p className="text-white/60 mb-4">
              We've received your Artist Membership interest. Our team will reach out to <strong className="text-white">{formData.email}</strong> with next steps to activate your membership.
            </p>
            <p className="text-white/40 text-sm mb-8">
              As a member, you'll get unlimited submissions, guaranteed rotation, your own artist profile page, and priority placement across the platform.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setMembershipRequested(false); setSelectedTier("free"); }} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-3 rounded-xl transition font-medium text-sm">
                Submit a Track Now (Free)
              </button>
              <button onClick={() => navigate("/")} className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl transition font-semibold text-sm">
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const style = TIER_STYLES[selectedTier];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Submit Your Track" description="Independent artists — submit your music to DJsandMCMedia for a chance to be featured on live channels and discovered by new listeners." />
      <Header />

      {/* Hero — Promotional Graphic */}
      <section className="pt-24 pb-8 px-6" data-testid="submit-hero">
        <div className="max-w-4xl mx-auto relative">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src="/artist-hero.png"
              alt="Artists — Get Heard Worldwide. Submit your music, reach 30+ countries, grow your fanbase."
              className="w-full"
              data-testid="submit-hero-image"
            />
            {/* Edge fades — blend into page background */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `
                linear-gradient(to bottom, #0a0a0a 0%, transparent 14%, transparent 78%, #0a0a0a 100%),
                linear-gradient(to right, #0a0a0a 0%, transparent 12%, transparent 88%, #0a0a0a 100%)
              `
            }} />
          </div>
          {/* CTA Button */}
          <div className="text-center mt-6">
            <button
              onClick={() => document.getElementById('tier-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold text-lg px-10 py-4 rounded-full transition-all hover:scale-105 shadow-lg shadow-purple-500/25"
              data-testid="hero-cta-btn"
            >
              Submit Your Music
            </button>
          </div>
        </div>
      </section>

      {/* ── TIER SELECTION ── */}
      <section className="pb-8 px-6" id="tier-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" data-testid="tier-heading">
            Choose How You Want to Get Heard
          </h2>
          <p className="text-white/40 text-sm text-center mb-8">Select your submission type below</p>

          <div className="grid md:grid-cols-3 gap-4">
            {TIERS.map((tier) => {
              const ts = TIER_STYLES[tier.id];
              const active = selectedTier === tier.id;
              return (
                <button
                  key={tier.id}
                  onClick={() => handleTierSelect(tier.id)}
                  className={`relative text-left p-6 rounded-2xl border-2 transition-all duration-200 ${
                    tier.id === "membership"
                      ? `md:scale-105 shadow-lg shadow-amber-500/15 ${active ? ts.activeBorder : "border-amber-400/25 hover:border-amber-400/50"}`
                      : `${active ? ts.activeBorder : ts.border}`
                  }`}
                  data-testid={`tier-${tier.id}`}
                >
                  {tier.badge && (
                    <span className={`absolute -top-3 right-4 text-[10px] font-bold px-3 py-1 rounded-full border ${ts.badge}`}>
                      {tier.badge}
                    </span>
                  )}

                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${ts.icon}`}>
                    <tier.icon className="w-5 h-5" />
                  </div>

                  <h3 className="text-white font-bold text-lg mb-1">{tier.label}</h3>
                  <p className={`font-semibold text-xl mb-4 ${ts.priceGlow || "text-white/70"}`}>{tier.priceLabel}</p>

                  <ul className="space-y-2.5 mb-5">
                    {tier.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/60 text-sm">
                        <span className={`mt-0.5 ${ts.check}`}>
                          <CheckCircle className="w-3.5 h-3.5" />
                        </span>
                        {perk}
                      </li>
                    ))}
                  </ul>

                  <div className={`w-full py-2.5 rounded-xl text-center text-sm font-semibold transition ${
                    active ? ts.button : "bg-white/5 text-white/50"
                  }`}>
                    {active ? tier.cta : tier.cta}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Urgency */}
          <div className="flex items-center justify-center gap-2.5 mt-8 bg-amber-500/10 border border-amber-500/20 rounded-full px-6 py-3 w-fit mx-auto" data-testid="urgency-line">
            <Clock className="w-4 h-4 text-amber-400 animate-pulse" />
            <span className="text-amber-300/90 text-sm font-medium">Limited artist rotation slots available weekly</span>
          </div>
        </div>
      </section>

      {/* ── FORM SECTION ── */}
      <section className="pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 md:p-10">

            {/* Membership CTA — show instead of form if membership selected */}
            {selectedTier === "membership" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-amber-400/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Crown className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Artist Membership</h2>
                <p className="text-white/70 font-semibold text-3xl mb-2">$19.99<span className="text-base text-white/40 font-normal">/mo</span></p>
                <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
                  Unlock unlimited submissions, guaranteed rotation, your own artist profile page, and priority placement across the entire DJsandMCMedia network.
                </p>

                <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6 text-left mb-6 max-w-sm mx-auto">
                  <h4 className="text-white/50 text-xs uppercase tracking-wider mb-3">Everything included:</h4>
                  <ul className="space-y-3">
                    {[
                      "Unlimited track submissions",
                      "Guaranteed rotation placement",
                      "Dedicated artist profile page",
                      "Priority placement in all feeds",
                      "Weekly play reports via email",
                      "Featured badge on every track",
                    ].map((perk, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-white/70 text-sm">
                        <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" /> {perk}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Collect name + email for interest */}
                <div className="space-y-3 max-w-sm mx-auto mb-6">
                  <input
                    type="text"
                    name="artist_name"
                    value={formData.artist_name}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-400/50 transition"
                    placeholder="Your artist name"
                    data-testid="membership-artist-name"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-400/50 transition"
                    placeholder="your@email.com"
                    data-testid="membership-email"
                  />
                  <input
                    type="text"
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-400/50 transition"
                    placeholder="Genre (Hip-Hop, R&B, etc.)"
                    data-testid="membership-genre"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-4 text-sm max-w-sm mx-auto" data-testid="membership-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <button
                  onClick={handleMembershipInterest}
                  disabled={submitting}
                  className="w-full max-w-sm bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-black font-bold py-4 rounded-xl transition-all text-lg disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
                  data-testid="membership-join-btn"
                >
                  <Crown className="w-5 h-5" />
                  {submitting ? "Submitting..." : "Join as Artist — $19.99/mo"}
                </button>
                <p className="text-white/30 text-xs mt-3">Our team will contact you to set up your membership and artist profile.</p>

                <div className="mt-6 pt-6 border-t border-white/5">
                  <button
                    onClick={() => handleTierSelect("free")}
                    className="text-white/30 hover:text-white/50 text-sm transition"
                  >
                    Or submit a track for free instead
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-2">Submit Your Track</h2>
                <p className="text-white/40 text-sm mb-8">
                  Must be ASCAP, BMI, or rights-cleared to submit. Limited artist slots available for rotation.
                </p>

                {error && (
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm" data-testid="submit-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Submitter Type */}
                  <div data-testid="submitter-type-toggle">
                    <label className="text-white/60 text-sm mb-2 block">I'm submitting as *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { value: "artist", label: "Artist" },
                        { value: "promoter", label: "Promoter" },
                        { value: "label", label: "Label" },
                        { value: "manager", label: "Manager" },
                      ].map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, submitter_type: type.value })}
                          className={`py-2.5 px-3 rounded-xl text-sm font-medium transition border ${
                            formData.submitter_type === type.value
                              ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                              : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80 hover:border-white/20'
                          }`}
                          data-testid={`submitter-type-${type.value}`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Promoter/Label/Manager Extra Fields */}
                  {formData.submitter_type !== "artist" && (
                    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 space-y-4" data-testid="rep-fields">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-white/60 text-sm mb-1.5 block">
                            {formData.submitter_type === "label" ? "Label Name *" : formData.submitter_type === "promoter" ? "Company Name *" : "Management Company *"}
                          </label>
                          <input type="text" name="company_name" value={formData.company_name} onChange={handleChange}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                            placeholder={formData.submitter_type === "label" ? "Label name" : "Company name"}
                            data-testid="submit-company-name" />
                        </div>
                        <div>
                          <label className="text-white/60 text-sm mb-1.5 block">Your Role *</label>
                          <select name="submitter_role" value={formData.submitter_role} onChange={handleChange}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                            data-testid="submit-role">
                            <option value="" className="bg-[#1a1a2e]">Select your role</option>
                            <option value="Promoter" className="bg-[#1a1a2e]">Promoter</option>
                            <option value="A&R" className="bg-[#1a1a2e]">A&R</option>
                            <option value="Manager" className="bg-[#1a1a2e]">Manager</option>
                            <option value="Label Rep" className="bg-[#1a1a2e]">Label Representative</option>
                            <option value="Publicist" className="bg-[#1a1a2e]">Publicist</option>
                            <option value="Other" className="bg-[#1a1a2e]">Other</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-white/60 text-sm mb-1.5 block">Authorization / Proof</label>
                        <input type="text" name="authorization_note" value={formData.authorization_note} onChange={handleChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                          placeholder="Link to management agreement, label page, or a note confirming authorization"
                          data-testid="submit-authorization" />
                        <p className="text-white/30 text-xs mt-1">Optional but helps speed up review.</p>
                      </div>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-white/60 text-sm mb-1.5 block">
                        {formData.submitter_type === "artist" ? "Artist / DJ Name *" : "Artist Name (who you're submitting for) *"}
                      </label>
                      <input type="text" name="artist_name" value={formData.artist_name} onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                        placeholder="Your artist name" data-testid="submit-artist-name" />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-1.5 block">Track Title *</label>
                      <input type="text" name="track_title" value={formData.track_title} onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                        placeholder="Song or mix title" data-testid="submit-track-title" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-white/60 text-sm mb-1.5 block">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                        placeholder="your@email.com" data-testid="submit-email" />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-1.5 block">Genre</label>
                      <input type="text" name="genre" value={formData.genre} onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                        placeholder="Hip-Hop, R&B, Soul, etc." data-testid="submit-genre" />
                    </div>
                  </div>

                  {/* ── TARGETING SECTION ── */}
                  <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="targeting-section">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-5 h-5 text-purple-400" />
                      <h3 className="text-white font-bold">Target Your Submission</h3>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => { setTargetType("general"); setSelectedStations([]); }}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition text-left ${
                          targetType === "general"
                            ? "border-purple-500/60 bg-purple-500/10"
                            : "border-white/10 bg-white/[0.02] hover:border-white/20"
                        }`}
                        data-testid="target-general"
                      >
                        <Globe className="w-5 h-5 flex-shrink-0" style={{ color: targetType === "general" ? "#a855f7" : "#71717a" }} />
                        <div>
                          <p className={`font-semibold text-sm ${targetType === "general" ? "text-white" : "text-white/70"}`}>General Pool</p>
                          <p className="text-white/40 text-xs">Auto-matched to stations by genre</p>
                        </div>
                        <span className="ml-auto text-xs font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Free</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setTargetType("specific")}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition text-left ${
                          targetType === "specific"
                            ? "border-amber-400/60 bg-amber-400/10"
                            : "border-white/10 bg-white/[0.02] hover:border-white/20"
                        }`}
                        data-testid="target-specific"
                      >
                        <Target className="w-5 h-5 flex-shrink-0" style={{ color: targetType === "specific" ? "#f59e0b" : "#71717a" }} />
                        <div>
                          <p className={`font-semibold text-sm ${targetType === "specific" ? "text-white" : "text-white/70"}`}>Target Stations</p>
                          <p className="text-white/40 text-xs">Pick stations directly</p>
                        </div>
                        <span className="ml-auto text-xs font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">Premium</span>
                      </button>
                    </div>

                    {targetType === "specific" && availableStations.length > 0 && (
                      <div className="space-y-2" data-testid="station-picker">
                        <p className="text-white/50 text-xs mb-2">Select stations to target:</p>
                        {availableStations.filter(s => s.name).map(station => {
                          const checked = selectedStations.includes(station.station_id);
                          return (
                            <label
                              key={station.station_id}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition ${
                                checked ? "bg-amber-400/10 border border-amber-400/30" : "bg-white/[0.02] border border-white/5 hover:border-white/15"
                              }`}
                              data-testid={`station-option-${station.station_id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStations([...selectedStations, station.station_id]);
                                  } else {
                                    setSelectedStations(selectedStations.filter(id => id !== station.station_id));
                                  }
                                }}
                                className="w-4 h-4 rounded accent-amber-400"
                              />
                              {station.logo_url ? (
                                <img src={station.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                  <Radio className="w-4 h-4 text-purple-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{station.name}</p>
                                {station.genres.length > 0 && (
                                  <p className="text-white/40 text-xs truncate">{station.genres.join(", ")}</p>
                                )}
                              </div>
                            </label>
                          );
                        })}
                        {selectedStations.length > 0 && (
                          <p className="text-amber-400 text-xs font-medium mt-2">
                            {selectedStations.length} station{selectedStations.length !== 1 ? "s" : ""} selected — priority placement
                          </p>
                        )}
                      </div>
                    )}

                    {/* BPM + Explicit */}
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="text-white/60 text-xs mb-1 block">BPM (optional)</label>
                        <input
                          type="number" min="40" max="220"
                          value={bpm} onChange={(e) => setBpm(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                          placeholder="e.g. 90"
                          data-testid="submit-bpm"
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="submit-explicit-toggle">
                          <input
                            type="checkbox"
                            checked={isExplicit}
                            onChange={(e) => setIsExplicit(e.target.checked)}
                            className="w-4 h-4 rounded accent-red-500"
                          />
                          <span className="text-white/60 text-sm">Contains explicit content</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Downloadable Track Link *</label>
                    <input type="url" name="streaming_link" value={formData.streaming_link} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                      placeholder="Google Drive, Dropbox, WeTransfer, etc." data-testid="submit-link" />
                    <p className="text-white/30 text-xs mt-1.5">Please provide a <span className="text-white/50 font-medium">downloadable link</span> (Google Drive, Dropbox, WeTransfer). Streaming-only links (Spotify, Apple Music) are for reference only and cannot be used for airplay.</p>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">PRO Affiliation *</label>
                    <select name="pro_affiliation" value={formData.pro_affiliation} onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                      data-testid="submit-pro">
                      <option value="" className="bg-[#1a1a2e]">Select your PRO</option>
                      <option value="ASCAP" className="bg-[#1a1a2e]">ASCAP</option>
                      <option value="BMI" className="bg-[#1a1a2e]">BMI</option>
                      <option value="SESAC" className="bg-[#1a1a2e]">SESAC</option>
                      <option value="Rights-Cleared" className="bg-[#1a1a2e]">Rights-Cleared (Independent)</option>
                      <option value="Other" className="bg-[#1a1a2e]">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Message (optional)</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm resize-none"
                      placeholder="Tell us about your music..." data-testid="submit-message" />
                  </div>

                  {/* Terms Agreement */}
                  <div onClick={() => setAgreedToTerms(!agreedToTerms)} className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition ${
                      agreedToTerms ? 'bg-purple-500 border-purple-500' : 'border-white/30 group-hover:border-white/50'
                    }`}>
                      {agreedToTerms && <span className="text-white text-xs font-bold">&#10003;</span>}
                    </div>
                    <p className="text-white/60 text-sm">
                      I agree to the{" "}
                      <a href="/submission-terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline" onClick={(e) => e.stopPropagation()}>
                        Submission Terms of Use
                      </a>
                      , including the license grant and content standards.
                    </p>
                  </div>

                  {/* Featured Indicator — if featured tier is selected */}
                  {selectedTier === "featured" && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="featured-selected-indicator">
                      <Star className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white text-sm font-semibold">Featured Submission — $10</p>
                        <p className="text-white/50 text-xs mt-0.5">You'll be prompted to pay via PayPal after submitting. Priority review + Featured badge + Top placement.</p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full disabled:opacity-50 font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg ${
                      selectedTier === "featured" ? style.button : "bg-purple-500 hover:bg-purple-600 text-white"
                    }`}
                    data-testid="submit-btn"
                  >
                    {submitting ? "Submitting..." : selectedTier === "featured" ? (
                      <><Star className="w-5 h-5" /> Submit & Get Featured</>
                    ) : (
                      <><Send className="w-5 h-5" /> Submit Your Track</>
                    )}
                  </button>

                  <p className="text-white/30 text-xs text-center" data-testid="submit-trust-line">
                    Free submission available. Quality-based selection only.
                  </p>
                </form>

                <p className="text-white/30 text-xs text-center mt-6">
                  For inquiries: gary@djsandmcs.com
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SubmitPage;
